import React, { useState } from "react";
import { Button, Card, CardBody, CardHeader, CardTitle, Input } from "reactstrap";
import { formatCurrency } from "../../../../utils/formatCurrency";
import LeafIcon from "../../../../components/LeafIcon";
import { formatDateLabel } from "../../../../utils/dateOnly";
import FonteBadge from "../../../../components/cotacao/FonteBadge";
import FreteSpotForm from "../../../../components/cotacao/FreteSpotForm";

// PO1-2905 (H4): texto do motivo, com a data que o usuário precisa para
// cobrar a tabela nova. Vem do backend como "tabela_vencida" ou
// "vigencia_futura".
//
// A data usa formatDateLabel (utils/dateOnly), NÃO `new Date(iso)` direto: o
// SQL entrega vigência como meia-noite UTC ("2026-07-31T00:00:00.000Z") e o
// construtor nativo converte para o fuso local, exibindo 30/07. O helper trata
// o trecho YYYY-MM-DD como data pura, que é o que vigência de tabela é.
const formatData = (valor) => (valor ? formatDateLabel(valor, "") : "");

// PO1-3917: km (e kg) inteiros em pt-BR, sem casas decimais ("1.250").
const formatKm = (valor) =>
  Math.round(Number(valor) || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 });

const motivoOmitida = (o) => {
  if (o.motivo === "vigencia_futura") {
    const inicio = formatData(o.dataValIni);
    return inicio ? `tabela só entra em vigência em ${inicio}` : "tabela ainda não vigente";
  }
  const fim = formatData(o.dataValFim);
  return fim ? `tabela vencida em ${fim}` : "tabela vencida";
};

const Step3Transportadora = ({
  proposals,
  indicador,
  transportadoraEscolhida,
  onSelectTransportadora,
  aproximado = false,
  onValorChange,
  omitidas = [],
  idEmbarcador,
  onAddSpot,
  onRemoveSpot,
}) => {
  // PO1-3200: formulário "+ Frete Spot" aberto/fechado.
  const [spotAberto, setSpotAberto] = useState(false);

  // Indicadores ANTT (por transportadora) + Carbono (do embarque). Chegam do
  // backend após a simulação (podem não ter carregado, ou sem opt-in ANTT).
  const anttHabilitado = Boolean(indicador && indicador.antt);
  const anttEstimado = anttHabilitado ? Boolean(indicador.antt.isEstimado) : false;
  const carbono = indicador && indicador.carbono ? indicador.carbono : null;
  const anttPorTransp = {};
  if (anttHabilitado && Array.isArray(indicador.antt.propostas)) {
    indicador.antt.propostas.forEach((p) => {
      if (p && p.idTransportadora != null) anttPorTransp[p.idTransportadora] = p;
    });
  }

  // Pill de conformidade ANTT por transportadora (verde Conforme / vermelho
  // Abaixo). "est." porque na cotação eixos/distância são estimados.
  const renderAnttPill = (idTransportadora) => {
    if (!anttHabilitado) return null;
    const a = anttPorTransp[idTransportadora];
    if (!a || !a.status || a.status === "nao_calculado") {
      return <span className="antt-pill antt-pill--nc">—</span>;
    }
    const piso = indicador.antt.pisoAntt;
    const idx = a.indiceAntt != null ? ` · índice ${a.indiceAntt.toFixed(2)}` : "";
    const title =
      (piso ? `Piso ANTT ${formatCurrency(piso)}` : "Piso ANTT") +
      idx +
      (anttEstimado ? " (estimado)" : "");
    return (
      <span className={`antt-pill antt-pill--${a.status}`} title={title}>
        {a.status === "conforme" ? "Conforme" : "Abaixo"}
        {anttEstimado ? <span className="antt-pill__est"> est.</span> : null}
      </span>
    );
  };

  return (
    <>
      <Card className="wizard-card">
        <CardHeader className="step3-header">
          <CardTitle tag="h6">Transportadoras Disponíveis</CardTitle>
          {onAddSpot && !spotAberto && (
            <Button
              color="primary"
              size="sm"
              outline
              className="btn-frete-spot"
              onClick={() => setSpotAberto(true)}
              title="Incluir transportadora da carteira com valor negociado"
            >
              + Frete Spot
            </Button>
          )}
          {carbono && carbono.emissaoKgCO2 != null && (
            <span
              className="nova-cotacao-carbono"
              title="Estimativa de emissão para o trajeto (rodoviário)"
            >
              <LeafIcon />~{Math.round(carbono.emissaoKgCO2)} kg CO₂
              {indicador && indicador.isEstimado ? (
                <span className="antt-pill__est"> est.</span>
              ) : null}
            </span>
          )}
        </CardHeader>
        <CardBody style={{ padding: 0 }}>
          {spotAberto && (
            <div style={{ padding: "0 12px" }}>
              <FreteSpotForm
                idEmbarcador={idEmbarcador}
                onAdd={(proposta) => {
                  onAddSpot(proposta);
                  setSpotAberto(false);
                }}
                onCancel={() => setSpotAberto(false)}
              />
            </div>
          )}
          {proposals.length === 0 ? (
            <p style={{ color: "#9a9a9a", padding: "20px", fontSize: 13 }}>
              {onAddSpot
                ? "Nenhuma transportadora retornou cotação. Use \"+ Frete Spot\" para incluir uma transportadora com valor negociado."
                : "Nenhuma proposta disponível. Volte e simule os fretes."}
            </p>
          ) : (
            <table className="proposals-list-table">
              <thead>
                <tr>
                  <th>Transportadora</th>
                  <th style={{ textAlign: "right" }}>Valor Total</th>
                  <th style={{ textAlign: "center" }}>Prazo</th>
                  {anttHabilitado && (
                    <th style={{ textAlign: "center" }}>ANTT</th>
                  )}
                  <th style={{ textAlign: "center" }}>Ação</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map((p, i) => {
                  const fonte = p.fonte || "tabela";
                  const isSelected =
                    transportadoraEscolhida?.transportadora === p.transportadora &&
                    (transportadoraEscolhida?.fonte || "tabela") === fonte;
                  return (
                    <tr
                      key={`${p.transportadora}||${fonte}`}
                      className={`${isSelected ? "selected-row" : ""}${i === 0 ? " best-row" : ""}`}
                    >
                      <td>
                        {i === 0 && (
                          <span className="badge-best-list" title="Melhor preço">
                            ★
                          </span>
                        )}
                        <span style={{ fontWeight: isSelected ? 700 : 500 }}>
                          {p.transportadora}
                        </span>
                        <FonteBadge fonte={fonte} />
                        {/* PO1-3915: a tabela cotou um veículo que não leva o
                            peso do embarque. Continua selecionável; vai ao fim. */}
                        {p.acimaCapacidade && (
                          <span
                            className="proposal-chip-capacidade"
                            title={
                              p.capacidadeKg != null
                                ? `Capacidade do veículo: ${formatKm(p.capacidadeKg)} kg`
                                : "O peso do embarque passa da capacidade do veículo"
                            }
                          >
                            Acima da capacidade do veículo
                          </span>
                        )}
                        {/* PO1-3303 (H1): a transportadora recebeu só peso total
                            e quantidade de caixas (sem as medidas de cada uma). */}
                        {p.modoCalculo === "pelo-total" && (
                          <div className="proposal-modo-nota">
                            Calculado pelo peso total, sem as medidas dos volumes
                          </div>
                        )}
                        {/* PO1-3917: adicional por km da tabela (km da rota e
                            de onde veio), ou o aviso quando o km não existe. */}
                        {p.km != null && (
                          <div className="proposal-modo-nota">
                            Inclui adicional por km: {formatKm(p.km)} km
                            {p.fonteKm ? ` (fonte: ${p.fonteKm})` : ""}
                          </div>
                        )}
                        {p.avisoKm && (
                          <div className="proposal-modo-nota">
                            km não informado: adicional por km não calculado
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <span className="proposal-valor">
                          {formatCurrency(p.valor)}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {p.prazo != null ? (
                          <span className="proposal-prazo">{p.prazo}d</span>
                        ) : (
                          <span className="proposal-prazo">—</span>
                        )}
                      </td>
                      {anttHabilitado && (
                        <td style={{ textAlign: "center" }}>
                          {renderAnttPill(p.idTransportadora)}
                        </td>
                      )}
                      <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        {isSelected ? (
                          <Button
                            color="success"
                            size="sm"
                            outline
                            onClick={() => onSelectTransportadora(null)}
                          >
                            Selecionado ✓
                          </Button>
                        ) : (
                          <Button
                            color="primary"
                            size="sm"
                            outline
                            onClick={() => onSelectTransportadora(p)}
                          >
                            Selecionar
                          </Button>
                        )}
                        {fonte === "spot" && onRemoveSpot && (
                          <Button
                            color="link"
                            size="sm"
                            className="btn-frete-spot-remover"
                            title="Remover frete spot"
                            onClick={() => onRemoveSpot(p)}
                          >
                            ✕
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {/* PO1-2905 (H4): transportadoras que têm tabela para esta rota mas
          ficaram de fora por vigência. Bloco informativo: sem valor e sem
          botão de selecionar — só existe para o usuário saber que a
          transportadora não sumiu por falta de cobertura. */}
      {omitidas.length > 0 && (
        <Card className="wizard-card">
          <CardHeader>
            <CardTitle tag="h6">Não cotadas</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Estas transportadoras têm tabela cadastrada para o destino, mas
              ficaram fora da cotação por vigência.
            </p>
            <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
              {omitidas.map((o) => (
                <li
                  key={`${o.idTransportadora}-${o.idTabela}`}
                  style={{ fontSize: 13, marginBottom: 4 }}
                >
                  <strong>{o.transportadora}</strong>
                  <span className="text-muted"> — {motivoOmitida(o)}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {transportadoraEscolhida && (
        <div className="chosen-transportadora-box">
          <div>
            <div className="chosen-label">
              Transportadora escolhida
              {aproximado && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--everlog-status-warning)",
                  }}
                >
                  · FRETE APROXIMADO (estimativa)
                </span>
              )}
            </div>
            <div className="chosen-name">{transportadoraEscolhida.transportadora}</div>
          </div>
          {aproximado ? (
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--everlog-card-muted)",
                  marginBottom: 2,
                }}
              >
                Valor estimado R$ (editável)
              </div>
              <Input
                type="text"
                inputMode="decimal"
                key={transportadoraEscolhida.transportadora}
                defaultValue={Number(
                  transportadoraEscolhida.valor || 0
                ).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                onBlur={(e) => onValorChange && onValorChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.target.blur();
                }}
                style={{ maxWidth: 180, textAlign: "right" }}
              />
            </div>
          ) : (
            <div className="chosen-valor">
              {formatCurrency(transportadoraEscolhida.valor)}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Step3Transportadora;
